import {
    NamedValueKey,
    PlatformMessage,
    PlatformPostMessageApi,
} from '@reachmedianetwork/post-message-client-rds-api';
import { assert } from '../Utility/assert';
import { PlayerAbstract } from './PlayerAbstract';
import { SignsMessenger, createSignsPostMessageClient } from './SignsPostMessageClient';
import type {
    BrightSignInformationInterface,
    ExecuteJavascriptOnWindow,
    GetRSFilePromiseSuccess,
    HeartbeatInterface,
    InstalledPackage,
    ListRSFilesPromiseSuccess,
    MultiWindowInterface,
    NtpInterface,
    PlayerControlInterface,
    PlayerPackageInterface,
    ReloadInterface,
    RequiredInterfaces,
    RootStorageFilesInterface,
    ScreenOrientationInterface,
    ScreenshotInterface,
    ScreenshotPromiseSuccess,
    setTimeZoneResponse,
    SignageReadyInterface,
    SystemUpdateObject,
    TeamviewerInterface,
    TimeZoneInterface,
    UpdateInterface,
    VideoAssetCacheInterface,
    VideoOutputInterface,
    WindowRectangle,
} from './interfaces';
import { moment } from '../../vendor/moment';
import { getVideoAssetUrl } from './Assets';
import { Heartbeat } from './Heartbeat';
import { PlatformError } from './Errors';
import { JsonValue } from '../Utility/JsonValue';

type OptionalPmcInterfaces = ScreenshotInterface &
    PlayerControlInterface &
    PlayerPackageInterface &
    NtpInterface &
    TimeZoneInterface &
    VideoAssetCacheInterface &
    HeartbeatInterface &
    VideoOutputInterface &
    RootStorageFilesInterface &
    ReloadInterface &
    UpdateInterface &
    TeamviewerInterface &
    BrightSignInformationInterface &
    ScreenOrientationInterface &
    SignageReadyInterface &
    MultiWindowInterface &
    ExecuteJavascriptOnWindow;

export class PlayerPmc extends PlayerAbstract implements RequiredInterfaces, OptionalPmcInterfaces {
    #pmsgr: SignsMessenger | null = null;
    #knownAssets: Map<string, string>;
    readonly #heartbeat: Heartbeat;

    constructor() {
        super();

        this.#knownAssets = new Map();

        this.#heartbeat = new Heartbeat({
            enable: () => this.postMessage(PlatformMessage.HeartbeatEnable, undefined),
            ping: () => this.postMessage(PlatformMessage.HeartbeatPing, undefined),
        });
    }

    async initializePlatform(): Promise<void> {
        if (this.#pmsgr === null) {
            this.#pmsgr = await this.#createSignsPostMessageClient();
        }

        await super.initializePlatform();

        if (this.supportsMessage(PlatformMessage.InitializePlatform)) {
            await this.postMessage(PlatformMessage.InitializePlatform, undefined);
        }
    }

    async #createSignsPostMessageClient(): Promise<SignsMessenger> {
        assert(window.top !== null, 'Cannot access parent window.');
        return createSignsPostMessageClient(window.parent);
    }

    protected async postMessage<T extends Parameters<SignsMessenger['client']['postMessage']>[0]>(
        type: T,
        payload: PlatformPostMessageApi[T]['requestPayload']
    ): Promise<PlatformPostMessageApi[T]['responsePayload']> {
        if (this.#pmsgr === null) {
            this.#pmsgr = await this.#createSignsPostMessageClient();
        }
        if (!this.#pmsgr.supports(type)) {
            throw new ReferenceError(`'${type}' not supported.`);
        }
        return this.#pmsgr.client.postMessage(type, payload);
    }

    supportsMessage(msgType: string): boolean {
        assert(this.#pmsgr !== null, 'Messenger not initialized.');
        return this.#pmsgr.supports(msgType);
    }

    async deleteNamedValue(keyName: NamedValueKey): Promise<void> {
        await this.postMessage(PlatformMessage.NamedValueDelete, { key: keyName });
    }

    getNamedValue(keyName: NamedValueKey): Promise<string | null>;
    getNamedValue(keyName: NamedValueKey, defaultValue: string): Promise<string>;
    async getNamedValue(keyName: NamedValueKey, defaultValue?: string): Promise<string | null> {
        const value = await this.postMessage(PlatformMessage.NamedValueGet, {
            key: keyName,
        });
        return value ?? defaultValue ?? null;
    }

    async setNamedValue(keyName: NamedValueKey, keyValue: string | unknown): Promise<void> {
        const value = String(keyValue);
        await this.postMessage(PlatformMessage.NamedValueSet, {
            key: keyName,
            value,
        });
    }

    async getScreenshot(): Promise<ScreenshotPromiseSuccess> {
        const { base64Image, mimeType } = await this.postMessage(
            PlatformMessage.ScreenshotGet,
            undefined
        );
        assert(mimeType === 'image/jpeg', 'Only JPEG images are currently supported.');
        return {
            base64Image,
            mimeType,
        };
    }

    async clearBrowserCacheOnReboot(): Promise<void> {
        await this.postMessage(PlatformMessage.ClearBrowserCacheOnReboot, undefined);
    }
    async rebootSystem(): Promise<void> {
        await this.setNamedValue(
            NamedValueKey.LastRestart,
            String(moment().format('YYYY-MM-DD HH:mm:ss'))
        );
        await this.postMessage(PlatformMessage.RebootSystem, undefined);
    }

    async getInstalledPackages(): Promise<Array<InstalledPackage>> {
        const installed = await this.postMessage(
            PlatformMessage.PlayerPackagesInstalled,
            undefined
        );
        return installed.map((pkg) => {
            return {
                Guid: pkg.guid,
                Name: pkg.name,
                VersionAsString: pkg.version,
                Description: pkg.description,
            };
        });
    }

    async deployPackage(
        notificationChannel: string,
        playerUuid: string,
        guid: string,
        version: string,
        purgeDownloadedPackage: boolean
    ): Promise<void> {
        await this.postMessage(PlatformMessage.PlayerPackagesDeploy, {
            notificationChannel,
            playerUuid,
            guid,
            version,
            purgeDownloadedPackage,
        });
    }

    async enableNtp(server: string, interval: number): Promise<void> {
        await this.postMessage(PlatformMessage.NtpEnable, {
            server,
            interval,
        });
    }
    async disableNtp(): Promise<void> {
        await this.postMessage(PlatformMessage.NtpDisable, undefined);
    }

    async setTimeZone(timeZone: string): Promise<setTimeZoneResponse> {
        const response = await this.postMessage(PlatformMessage.TimeZoneSet, { timeZone });
        return {
            rebootRequired: response.restartRequired,
        };
    }

    async cacheVideoAsset(assetUuid: string): Promise<boolean> {
        let result;

        const response = await this.postMessage(PlatformMessage.AssetStatus, {
            cacheId: assetUuid,
        });
        switch (response.status) {
            case 'cached':
                result = true;
                // already cached
                break;
            case 'caching':
                result = false;
                // nothing needs doing
                break;
            case 'not-cached': {
                result = false;
                // start caching asset
                const videoUrl = await getVideoAssetUrl(assetUuid); // TODO: pass url via parameter
                await this.postMessage(PlatformMessage.AssetDownload, {
                    url: videoUrl,
                    cacheId: assetUuid,
                });
                this.#knownAssets.set(assetUuid, videoUrl);
                break;
            }
        }

        return result;
    }

    async getVideoPath(assetUuid: string): Promise<string> {
        const response = await this.postMessage(PlatformMessage.AssetStatus, {
            cacheId: assetUuid,
        });
        if (response.status === 'cached' && response.localUrl !== null) {
            return response.localUrl;
        }

        const videoUrl = this.#knownAssets.get(assetUuid);
        assert(videoUrl !== undefined, `Unknown asset ${assetUuid}`);
        return videoUrl;
    }

    async isCachedAsset(assetUuid: string): Promise<boolean> {
        const response = await this.postMessage(PlatformMessage.AssetStatus, {
            cacheId: assetUuid,
        });
        return response.status === 'cached';
    }

    async startSendingHeartbeat(): Promise<void> {
        await this.#heartbeat.start();
    }

    async getPlayerVersion(): Promise<string> {
        const response = await this.postMessage(PlatformMessage.PlayerInformationGet, undefined);
        return response.version;
    }

    async reloadSignage(): Promise<void> {
        return this.postMessage(PlatformMessage.ReloadSignage, undefined);
    }

    async getRootStorageFile(fiName: string): Promise<GetRSFilePromiseSuccess> {
        const { fileBase64 } = await this.postMessage(PlatformMessage.RootStorageFileGet, {
            fileName: fiName,
        });
        return { fiString: fileBase64 };
    }

    async getListOfRootStorageFiles(): Promise<ListRSFilesPromiseSuccess> {
        const { files } = await this.postMessage(PlatformMessage.RootStorageFileList, undefined);
        return { diFiles: files };
    }

    schedulePlatformUpdate(systemUpdate: SystemUpdateObject): Promise<void> {
        return this.postMessage(PlatformMessage.SchedulePlatformUpdate, {
            day: systemUpdate.day,
            time: systemUpdate.time,
            enabled: systemUpdate.isEnabled,
        });
    }

    async disableVideoOutput(disableOutput?: boolean): Promise<boolean> {
        const state = disableOutput === false ? 'enabled' : 'disabled';
        await this.postMessage(PlatformMessage.VideoOutputSet, { videoOutput: state });
        return state === 'disabled';
    }

    async getRemoteSupportDeviceId(): Promise<string> {
        const remoteSupportDeviceId = await this.postMessage(
            PlatformMessage.RemoteSupportDeviceIdGet,
            undefined
        );
        if (remoteSupportDeviceId !== null) {
            return remoteSupportDeviceId;
        }

        throw new PlatformError(
            'NamedValueCache key remote_support_device_id is not defined or TeamViewer instance does not belong to REACH'
        );
    }

    async getBsDeviceInfo(): ReturnType<BrightSignInformationInterface['getBsDeviceInfo']> {
        const brightsignInfo = await this.postMessage(PlatformMessage.BsDeviceInfoGet, undefined);
        return {
            family: brightsignInfo.family,
            deviceUniqueId: brightsignInfo.deviceUniqueId,
            bootVersion: brightsignInfo.bootVersion,
            deviceBootCount: brightsignInfo.deviceBootCount,
            deviceLifetime: brightsignInfo.deviceLifetime,
            deviceUptime: brightsignInfo.deviceUptime,
            version: brightsignInfo.version,
            model: brightsignInfo.model,
        };
    }

    async setOrientation(orientation: 'Default' | 'Clockwise90' | 'Clockwise270'): Promise<void> {
        await this.postMessage(PlatformMessage.ScreenConfigurationSet, {
            rotation: orientation,
            delayRestart: false,
        });
    }

    async sendSignageReadySignal(): Promise<void> {
        await this.postMessage(PlatformMessage.SignageReady, undefined);
    }

    async executeJsOnWindow(handleId: number, jsToExecute: string): Promise<JsonValue> {
        const { jsonEncodedResult, status, errorMessage } = await this.postMessage(
            PlatformMessage.ExecuteJavascriptOnWindow,
            {
                handleId,
                jsToExecute,
            }
        );

        if (status === 'failure') {
            throw new Error(errorMessage);
        }

        return JSON.parse(jsonEncodedResult) as JsonValue;
    }

    async createWindow({
        rectangle,
        url,
    }: {
        rectangle: WindowRectangle;
        url: string;
    }): Promise<number> {
        const { handleId } = await this.postMessage(PlatformMessage.CreateWindow, {
            rectangle,
            url,
        });
        return handleId;
    }

    async destroyWindow(handleId: number): Promise<boolean> {
        const { success } = await this.postMessage(PlatformMessage.DestroyWindow, { handleId });
        return success;
    }
}
